import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type { ICart, IIngkaToken, IShoppingItem } from '@inter-ikea-kompis/types';

import ServiceFetch from '../../../../utilities/ServiceFetch.js';
import IngkaCartErrorCodeEnum from './enums/IngkaCartErrorCodeEnum.js';
import type IIngkaGetCartErrorResponse from './types/IIngkaGetCartErrorResponse.js';
import type IIngkaGetCartResponse from './types/IIngkaGetCartResponse.js';

/**
 * This class handles adding items to the One Web cart.
 */
export default class IngkaCartService {
	private readonly endpoint: string;
	private readonly itemGroupingInBagEnabled: boolean;
	private readonly consumerName: string;

	private get endpointLowerCase(): string {
		return this.endpoint.toLowerCase();
	}

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.endpoint Endpoint.
	 * @param options.consumerName Consumer used to communicate with one web cart.
	 * @param options.itemGroupingInBagEnabled Setting if grouping in bag is enabled on the market.
	 */
	constructor(options: {
		endpoint: string;
		itemGroupingInBagEnabled: boolean;
		consumerName: string;
	}) {
		this.endpoint = options.endpoint;
		this.itemGroupingInBagEnabled = options.itemGroupingInBagEnabled;
		this.consumerName = options.consumerName;
	}

	/**
	 * Adds items to the shopping cart.
	 *
	 * @param options Options.
	 * @param options.items Array of shopping items.
	 * @param options.token One web token.
	 * @param options.designCode Design code (VPC-code) to group articles in checkout and assembly services.
	 * @param options.addDesignCodeAsGroup Boolean to group the articles or not in cart.
	 * @param options.singleItems Single items to be added individually outside the group.
	 * @returns Promise object.
	 */
	public async addToCart(options: {
		items: IShoppingItem[];
		token: IIngkaToken;
		designCode?: string;
		addDesignCodeAsGroup?: boolean;
		singleItems?: IShoppingItem[];
	}): Promise<ICart> {
		const { designCode, addDesignCodeAsGroup, items, token, singleItems } = options;

		if (!designCode && addDesignCodeAsGroup) {
			throw new ServiceException(
				"Can't add as a group to cart when no design code has been provided."
			);
		}
		const addItemsAsGroup = !!designCode && !!addDesignCodeAsGroup && this.itemGroupingInBagEnabled;

		let response;
		if (addItemsAsGroup) {
			/**
			 * If the design code is already in cart, don't do anything.
			 * TODO: This logic should be removed when the INGKA front-end team has implemented support for quantity for design codes.
			 */
			const inCart = await this.checkIfInCart({ token: options.token, designCode });
			if (inCart.isInCart) {
				return {
					cartCount: inCart.cartQuantity ?? null,
					errorList: []
				};
			}

			response = await this.addDesignCodeToCart(designCode, token);
			if (singleItems && singleItems.length > 0) {
				response = await this.addItemsToCart(singleItems, token);
			}
		} else {
			response = await this.addItemsToCart(items, token);
		}

		return {
			cartCount: response.cart?.quantity ?? null,
			// The request is always expected to throw an error if the request fails. Therefore, when returning here the errorList will always be empty.
			errorList: []
		};
	}

	private async addItemsToCart(items: IShoppingItem[], token: IIngkaToken): Promise<object> {
		const requestBody = {
			items: items.map((item) => {
				return {
					itemNo: item.id,
					quantity: item.quantity
				};
			}),
			populateCart: {
				fetchCartContext: true,
				fetchItemInfo: true
			}
		};
		return await ServiceFetch.json(this.endpointLowerCase + '/items', {
			body: JSON.stringify(requestBody),
			method: 'POST',
			headers: this.getHeaders(token)
		});
	}

	private async addDesignCodeToCart(designCode: string, token: IIngkaToken): Promise<object> {
		const requestBody = {
			plans: [{ code: designCode, quantity: 1 }],
			populateCart: {
				fetchCartContext: true,
				// Needed to be able to add plans to cart, for some reason.
				fetchItemInfo: true
			}
		};

		return await ServiceFetch.json(this.endpointLowerCase + '/plans', {
			body: JSON.stringify(requestBody),
			method: 'POST',
			headers: this.getHeaders(token)
		});
	}

	/**
	 * Returns cart count for a shopping cart.
	 *
	 * @param options Options.
	 * @param options.token One web token.
	 * @returns Promise object.
	 */
	public async getCartCount(options: { token: IIngkaToken }): Promise<number> {
		const cart = await this.getCart(options);
		return cart ? cart.quantity : 0;
	}

	private async checkIfInCart(options: {
		token: IIngkaToken;
		designCode: string;
	}): Promise<{ isInCart: false } | { isInCart: true; cartQuantity: number }> {
		const cart = await this.getCart(options);

		if (!cart) {
			return { isInCart: false };
		}

		for (const group of cart.groups) {
			for (const plan of group.plans) {
				if (plan.code.toUpperCase() === options.designCode.toUpperCase()) {
					return { isInCart: true, cartQuantity: cart.quantity };
				}
			}
		}

		return { isInCart: false };
	}

	private async getCart(options: { token: IIngkaToken }): Promise<IIngkaGetCartResponse | null> {
		try {
			const response = await ServiceFetch.json(this.endpointLowerCase + '?fetchItemInfo=true', {
				method: 'GET',
				headers: this.getHeaders(options.token)
			});
			return <IIngkaGetCartResponse>response;
		} catch (error) {
			const errorResponse = await error.serverResponse?.json();
			if (errorResponse?.errors) {
				const ingkaServerResponse = errorResponse as IIngkaGetCartErrorResponse;
				for (const error of ingkaServerResponse.errors) {
					if (error.code === IngkaCartErrorCodeEnum.cartNotFound) {
						return null;
					}
				}
			}

			throw error;
		}
	}

	/**
	 * Returns headers.
	 *
	 * @param token Token.
	 * @returns Headers.
	 */
	private getHeaders(token: IIngkaToken): HeadersInit {
		const headers = {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			'Accept-Language': 'en',
			Authorization: 'Bearer ' + token.value,
			'X-Consumer-Name': this.consumerName
		};

		return headers;
	}
}
